import { client } from "@/api/client.gen";

export type LocaleCode = "da" | "en";

export const DEFAULT_LOCALE: LocaleCode = "da";
export const SUPPORTED_LOCALES: LocaleCode[] = ["da", "en"];
const STORAGE_KEY = "locale";

let catalog: Record<string, string> = {};

export function setCatalog(strings: Record<string, string>) {
    catalog = strings;
}

export function isLocale(value: string | null | undefined): value is LocaleCode {
    return value === "da" || value === "en";
}

export function detectLocale(): LocaleCode {
    if (typeof window === "undefined") return DEFAULT_LOCALE;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("da")) return "da";
    if (nav.startsWith("en")) return "en";
    return DEFAULT_LOCALE;
}

export function persistLocale(locale: LocaleCode) {
    localStorage.setItem(STORAGE_KEY, locale);
}

function interpolate(template: string, vars?: Record<string, string | number>) {
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (match, key: string) =>
        vars[key] !== undefined ? String(vars[key]) : match,
    );
}

/** Translate a catalog key. Falls back to the key if it is not in the loaded
 * database catalog. Expected keys are listed in
 * `frontend/src/app/workspace/i18n/i18nKeyRegistry.ts`; this module only
 * interpolates values that have been registered in `public.i18n`. */
export function t(key: string, vars?: Record<string, string | number>): string {
    const value = catalog[key];
    return interpolate(value ?? key, vars);
}

/** True when the loaded database catalog has this key (including empty string).
 * Unregistered expected keys are not present here — see i18nKeyRegistry.ts. */
export function hasI18nKey(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(catalog, key);
}

export async function fetchStrings(locale: LocaleCode): Promise<Record<string, string>> {
    const response = await client.get({
        url: `/api/i18n/strings?locale=${encodeURIComponent(locale)}`,
    });
    const data = response.data as { strings?: Record<string, string> } | undefined;
    return data?.strings ?? {};
}

export async function persistUserLocale(locale: LocaleCode): Promise<void> {
    await client.post({
        url: "/api/users/set-locale",
        body: { locale },
        headers: { "Content-Type": "application/json" },
    });
}

export function applyDocumentLocale(locale: LocaleCode, strings: Record<string, string>) {
    document.documentElement.lang = locale;
    const title = strings["seo.documentTitle"];
    if (title) document.title = title;
}
